import React, { useEffect, useMemo, useState } from "react";
import { Col, Row } from "reactstrap";
import { useSelector } from "react-redux";
import { InputAdornment, Typography } from "@mui/material";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";

import {
  getOptionLabel,
  ltLoadOptions,
  loadOptions,
  gstOptions,
  getOptionValue,
} from "common/enums";
import SelectField from "Components/Common/SelectField";
import InputFieldText from "Components/Common/InputFieldText";

import { onlyNumberKeys } from "utils";

import { usePremiseTypeQuery } from "apis/PremiseType";
import { useConsumersQuery } from "apis/User";
import { useApplicantTypeQuery } from "apis/ApplicantType";
import { CONSUMER } from "common/constant";

const premiseNames = ["agriculture", "temporary"];

const htLoadOptions = [{ value: 2, label: "Three Phase" }];

const tentionOptions = [
  { value: "LT", label: "LT" },
  { value: "HT", label: "HT" },
];

const SINGLE_PHASE = 1;
const THREE_PHASE = 2;

const defaultSupplyVoltageOptions = [
  { value: 33, label: "33 kV" },
  { value: 11, label: "11 kV" },
  { value: 415, label: "415 V" },
  { value: 240, label: "240 V" },
];

const singlePhaseOptions = [{ value: 240, label: "240 V" }];

const ShiftingOfServiceForm = ({ formik, t }) => {
  const { roles } = useSelector((state) => state.Login);

  const { data: { data: { items: applicantType } = {} } = {} } =
    useApplicantTypeQuery();
  const [premiseIds, setPremiseIds] = useState([]);
  const [supplyVoltageOptions, setSupplyVoltageOptions] = useState([]);

  const {
    values: {
      id,
      consumerNumber,
      applicationTypeId,
      applicant: { applicantTypeId, isGSTRegistered },
      connectionLoad: {
        powerRequirement,
        totalPowerRequirement,
        loadDetails,
        tention,
        supplyVoltage,
      },
      extraValues: { isPanNumber, isGstNumber },
      inProgressApplicationId,
      isAllApplicationsProcessCompleted,
      payableBillAmount,
    },
    setFieldValue,
  } = formik;

  const { data: { data: consumersOptions } = {} } = useConsumersQuery({
    applicationType: applicationTypeId,
    utilityId: getOptionValue("utilityOptions", "Power"),
  });

  const threePhaseOptions = useMemo(
    () =>
      tention === tentionOptions[1].value
        ? [
            { value: 33, label: "33 kV" },
            { value: 11, label: "11 kV" },
            { value: 415, label: "415 V" },
          ]
        : [{ value: 415, label: "415 V" }],
    [tention]
  );

  const { data: { data: premiseType } = {} } = usePremiseTypeQuery({
    utilityId: 1,
  });

  const isIndividualApplicantType = useMemo(
    () =>
      applicantType?.find(
        (application) => application.name.toLowerCase() === "individual"
      ).id === applicantTypeId,
    [applicantType, applicantTypeId]
  );

  const isDisable = useMemo(
    () =>
      (inProgressApplicationId !== id && !isAllApplicationsProcessCompleted) ||
      payableBillAmount > 0,
    [
      inProgressApplicationId,
      id,
      isAllApplicationsProcessCompleted,
      payableBillAmount,
    ]
  );

  useEffect(() => {
    const updatedPremiseIds = [];
    premiseType?.forEach((premise) => {
      if (premiseNames.includes(premise.name.toLowerCase())) {
        updatedPremiseIds.push(premise.id);
      }
    });

    setPremiseIds(updatedPremiseIds);
  }, [premiseType]);

  useEffect(() => {
    if (
      formik.values.connectionLoad.increaseOfLoad &&
      formik.values.connectionLoad.powerRequirement
    ) {
      formik.setFieldValue(
        "connectionLoad.totalPowerRequirement",
        formik.values.connectionLoad.increaseOfLoad +
          formik.values.connectionLoad.powerRequirement
      );
    } else {
      formik.setFieldValue(
        "connectionLoad.totalPowerRequirement",
        formik.values.connectionLoad.powerRequirement
      );
    }
  }, [formik.values.connectionLoad.increaseOfLoad]);

  useEffect(() => {
    const options = tention
      ? tention === tentionOptions[0].value && totalPowerRequirement <= 6
        ? ltLoadOptions
        : htLoadOptions
      : [];
    if (loadDetails && options.find((option) => option.value !== loadDetails)) {
      setFieldValue("connectionLoad.loadDetails", "");
    }
  }, [tention, totalPowerRequirement]);

  useEffect(() => {
    const options =
      loadDetails === SINGLE_PHASE
        ? singlePhaseOptions
        : loadDetails === THREE_PHASE
        ? threePhaseOptions
        : defaultSupplyVoltageOptions;
    if (
      supplyVoltage &&
      !options.some((option) => option.value === supplyVoltage)
    ) {
      setFieldValue("connectionLoad.supplyVoltage", "");
    }
    setSupplyVoltageOptions(options);
  }, [loadDetails, tention]);

  return (
    <>
      <Row>
        {roles.includes(CONSUMER) && (
          <Col md={4} sm={6} className="mt-3">
            <SelectField
              label={t("Customer Number") + "*"}
              name="utilityConsumerId"
              formik={formik}
              options={consumersOptions || []}
              getOptionLabel={(option) => option.consumerNumber}
              getOptionValue={(option) => option.id}
            />
          </Col>
        )}
        <Typography className="mb-3" lineHeight="1" variant="h6">
          {t("Customer Details")}
        </Typography>

        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Customer Name")}
            name="consumerName"
            formik={formik}
            disabled
          />
        </Col>

        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Current Tariff Category")}
            name="tariffName"
            formik={formik}
            disabled
          />
        </Col>

        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Current Usage Purpose")}
            name="utilityAddress.premiseUsagePurpose"
            formik={formik}
            disabled
          />
        </Col>
        {!isPanNumber && (
          <Col md={4} sm={6}>
            <InputFieldText
              label={
                isIndividualApplicantType
                  ? t("PAN Number")
                  : t("PAN Number") + "*"
              }
              name="applicant.panNumber"
              formik={formik}
              disabled={isDisable}
              onChange={(e) =>
                setFieldValue(
                  "applicant.panNumber",
                  e.target.value.toUpperCase()
                )
              }
              placeholder={
                isIndividualApplicantType
                  ? t("PAN Number")
                  : t("PAN Number") + "*"
              }
            />
          </Col>
        )}
        {!isGstNumber && (
          <Col md={4} sm={6}>
            <SelectField
              label={t("GST") + "*"}
              name="applicant.isGSTRegistered"
              onChange={(_, { value }) => {
                setFieldValue("applicant.isGSTRegistered", value);
                if (!value) {
                  setFieldValue("applicant.gstNumber", "");
                }
              }}
              formik={formik}
              disabled={isDisable}
              options={gstOptions}
            />
          </Col>
        )}
        {!isGstNumber && isGSTRegistered && (
          <Col md={4} sm={6}>
            <InputFieldText
              label={t("GST Number") + "*"}
              name="applicant.gstNumber"
              formik={formik}
              onChange={(e) =>
                setFieldValue(
                  "applicant.gstNumber",
                  e.target.value.toUpperCase()
                )
              }
              placeholder="GST Number"
              disabled={isDisable}
            />
          </Col>
        )}

        <Typography className="mb-3" lineHeight="1" variant="h6">
          {t("Load Details")}
        </Typography>

        <Col md={4}>
          <SelectField
            label={t("Phase")}
            name="connectionLoad.loadDetails"
            formik={formik}
            options={loadOptions}
            getOptionLabel={(option) => option.label}
            disabled
          />
        </Col>
        <Col md={4}>
          <SelectField
            label={t("Supply Voltage")}
            name="connectionLoad.supplyVoltage"
            formik={formik}
            options={supplyVoltageOptions}
            getOptionLabel={(option) => option.label}
            getOptionValue={(option) => option.value}
            placeholder="Select Supply Voltage"
            disabled
          />
        </Col>
        <Col md={4}>
          <InputFieldText
            label={t("Total Power Requirement")}
            name="connectionLoad.powerRequirement"
            formik={formik}
            type="number"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {getOptionLabel(
                    "unitOptions",
                    tention === tentionOptions[0].value &&
                      premiseIds.includes(
                        formik.values.utilityAddress.premiseTypeId
                      )
                      ? powerRequirement <= 125
                        ? 3
                        : 2
                      : powerRequirement < 151
                      ? 1
                      : 2
                  )}
                </InputAdornment>
              ),
              inputProps: { min: 0, onKeyDown: onlyNumberKeys },
            }}
            placeholder={t("Enter Contract Load")}
            disabled
          />
        </Col>
      </Row>
      {!!inProgressApplicationId &&
      inProgressApplicationId !== id &&
      !isAllApplicationsProcessCompleted &&
      !!consumerNumber ? (
        <Typography variant="body2" color="error">
          There are some incomplete applications associated with this consumer
          number. You are unable to fill the form.
        </Typography>
      ) : payableBillAmount > 0 ? (
        <Typography variant="body2" color="error">
          You have an unpaid amount of {payableBillAmount} RS. Please pay first
          to continue.
        </Typography>
      ) : null}
    </>
  );
};

ShiftingOfServiceForm.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};
export default withTranslation()(ShiftingOfServiceForm);
